import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  useGetCompanyQuery,
  useUpdateCompanyMutation,
  useUpdateCompanyDraftMutation,
  useUpdateCompanyDraftEditMutation,
  useFetchCompanyDraftEditMutation,
  useGetCompanyRequestStatusMutation,
  useUpdateCompanyRequestStatusMutation,
} from "../../../../store/Company/companyApiSlice";
import { setCurrentEditingCompany } from "../../../../store/Company/companySlice";
import useCompanyUpdate from "../../Dashboard/mycompanies/CompleteCompanyProfile/useCompanyFollowUpdate";
import { savePath, nextStatus } from "./companyDraftSave";
import {
  SOCIAL_KEYS,
  socialDisplayStatus,
  socialPayload,
  socialValues,
} from "./socialFields";

/* The Social Media section's behaviour. */
export const useSocialSection = ({ onSaved }) => {
  const dispatch = useDispatch();
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);

  const companyID = currentEditingCompany?.companyID;

  /* Transcribed: this section reads its values from its OWN company query, not
     from the store — the store's copy can be a stale draft-merged record. */
  const { data, refetch } = useGetCompanyQuery({ token, companyId: companyID });

  const [values, setValues] = useState(() =>
    SOCIAL_KEYS.reduce((acc, k) => ({ ...acc, [k]: "" }), {})
  );
  const [draft, setDraft] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState("pending");
  const [requestID, setRequestID] = useState(null);

  const [updateCompany, { isLoading }] = useUpdateCompanyMutation();
  const [updateCompanyDraft] = useUpdateCompanyDraftMutation();
  const [updateCompanyDraftEdit] = useUpdateCompanyDraftEditMutation();
  const [fetchCompanyDraftEdit] = useFetchCompanyDraftEditMutation();
  const [getCompanyRequestStatus] = useGetCompanyRequestStatusMutation();
  const [updateCompanyRequestStatus] = useUpdateCompanyRequestStatusMutation();

  const handleUpdateCompanyFollow = useCompanyUpdate();

  /* Transcribed: the draft's values win, falling back to the record's. */
  const loadDraft = async () => {
    try {
      const fetched = await fetchCompanyDraftEdit({ companyId: companyID, token }).unwrap();
      if (fetched?.data) {
        const row = fetched.data[0];
        setDraft(row);
        setValues(socialValues(row, data));
      }
    } catch {
      /* Swallowed in the live code. With no draft the fields keep the record's
         own values, which the branch below has already set. */
    }
  };

  useEffect(() => {
    if (!currentEditingCompany) return;

    /* Transcribed: the latest request for THIS company decides which save path
       a submit takes. */
    if (currentEditingCompany?.userCompany) {
      (async () => {
        const individualID =
          localStorage.getItem("userindividualID") ||
          currentEditingCompany?.userCompany?.[0]?.individualID?.individualID;

        const res = await getCompanyRequestStatus({
          companyID: currentEditingCompany.companyID,
          individualID,
          token,
        });

        const rows = res?.data?.filter(
          (item) => item?.companyID?.companyID === currentEditingCompany.companyID
        );
        if (rows?.length) {
          const latest = rows[rows.length - 1];
          setVerificationStatus(latest?.status);
          setRequestID(latest?.requestID);
        }
      })();
    }

    /* Transcribed branch: a Modified request reads the draft, anything else
       reads the company query's own result. */
    if (verificationStatus === "Modified") {
      loadDraft();
    } else if (data) {
      setValues(socialValues(data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEditingCompany, verificationStatus, data]);

  const setField = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const submit = async () => {
    const path = savePath({ draft, verificationStatus });

    try {
      if (path === "draft") {
        /* Transcribed order: the outstanding request is closed first. */
        await updateCompanyRequestStatus({ requestID, token }).unwrap();
        const res = await updateCompanyDraft({
          data: { companyID, ...socialPayload(values, "Modified") },
          token,
        }).unwrap();
        if (res) onSaved?.();
        return;
      }

      if (path === "draftEdit") {
        const res = await updateCompanyDraftEdit({
          data: socialPayload(values, "Modified"),
          companyId: companyID,
          token,
          /* Transcribed: names the draft slice being edited. */
          type: "socialmedia",
        }).unwrap();
        if (res) onSaved?.();
        return;
      }

      const res = await updateCompany({
        data: socialPayload(
          values,
          nextStatus(currentEditingCompany?.social_media_status)
        ),
        token,
        companyId: companyID,
      }).unwrap();

      if (res) {
        dispatch(setCurrentEditingCompany(res));
        /* Transcribed: followers are told, and a failure is logged rather than
           blocking the save. */
        const result = await handleUpdateCompanyFollow(
          companyID,
          "Social Media Updated",
          token
        );
        if (!result.success) console.error(result.error);
        onSaved?.();
        refetch();
      }
    } catch (error) {
      /* The live code swallows all three. Written down instead — F-040 covers
         the same gap across this flow — and the flow does not advance. */
      console.error("Social media save failed:", error);
    }
  };

  return {
    values,
    setField,
    isLoading,
    submit,
    displayStatus: socialDisplayStatus(currentEditingCompany, draft),
  };
};

export default useSocialSection;
